import { eventBus } from "@/eventBus";

const MIME_PDF = "application/pdf";
const MIME_XLSX =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const MIME_XLSM = "application/vnd.ms-excel.sheet.macroEnabled.12";
const MIME_XLS = "application/vnd.ms-excel";

export const DOWNLOAD_LIMIT_EMAIL = "amemon@fluidsdata.com";
export const DOWNLOAD_LIMIT_MAILTO_SUBJECT = "PDF Download Limit Reached";

const DEFAULT_LIMIT_MESSAGE =
  "You have reached the free download limit on your trial account.";

function baseContentType(response) {
  const h =
    response.headers["content-type"] ||
    response.headers["Content-Type"] ||
    "";
  return h.split(";")[0].trim().toLowerCase();
}

function extensionToMime(ext) {
  const e = (ext || "pdf").toLowerCase();
  if (e === "xlsx") return MIME_XLSX;
  if (e === "xlsm") return MIME_XLSM;
  if (e === "xls") return MIME_XLS;
  return MIME_PDF;
}

async function sniffBlobType(data, preferredMime) {
  if (!data || typeof data.slice !== "function") {
    return preferredMime;
  }
  const n = Math.min(4, data.size || 0);
  if (n < 2) {
    return preferredMime;
  }
  const buf = await data.slice(0, n).arrayBuffer();
  const u = new Uint8Array(buf);
  if (
    u.length >= 4 &&
    u[0] === 0x25 &&
    u[1] === 0x50 &&
    u[2] === 0x44 &&
    u[3] === 0x46
  ) {
    return MIME_PDF;
  }
  if (u.length >= 2 && u[0] === 0x50 && u[1] === 0x4b) {
    return MIME_XLSX;
  }
  return preferredMime;
}

export function isDownloadLimitError(message, statusCode, httpStatus) {
  const code = Number(statusCode ?? httpStatus);
  if (code === 429 || code === 403) {
    return true;
  }
  const normalized = String(message || "").toLowerCase();
  return (
    normalized.includes("download limit") ||
    normalized.includes("limit reached") ||
    normalized.includes("free download") ||
    normalized.includes("trial account") ||
    normalized.includes("maximum download") ||
    normalized.includes("reached the") && normalized.includes("limit")
  );
}

export function showDownloadLimitModal(message) {
  eventBus.emit("showDownloadLimitModal", {
    message: message || DEFAULT_LIMIT_MESSAGE,
  });
}

export function openDownloadLimitEmail() {
  const subject = encodeURIComponent(DOWNLOAD_LIMIT_MAILTO_SUBJECT);
  const body = encodeURIComponent(
    "Hi Fluidsdata Team,\n\nI have reached the free PDF download limit on my trial account and would like to request additional access.\n\nThank you."
  );
  window.open(
    `mailto:${DOWNLOAD_LIMIT_EMAIL}?subject=${subject}&body=${body}`,
    "_blank",
    "noopener,noreferrer"
  );
}

export async function parseDownloadErrorPayload(data, httpStatus) {
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const j = JSON.parse(text);
      return {
        message: j?.message || null,
        statusCode: j?.statusCode ?? httpStatus ?? null,
      };
    } catch {
      return { message: null, statusCode: httpStatus ?? null };
    }
  }
  if (data && typeof data === "object") {
    return {
      message: data.message || null,
      statusCode: data.statusCode ?? httpStatus ?? null,
    };
  }
  return { message: null, statusCode: httpStatus ?? null };
}

function resolveDownloadFailure(message, statusCode, httpStatus) {
  if (isDownloadLimitError(message, statusCode, httpStatus)) {
    showDownloadLimitModal(message);
    return { limitModal: true };
  }
  return { snackbarMessage: message || null };
}

export async function blobFromSampleReportResponse(response, extensionGuess) {
  const preferredMime = extensionToMime(extensionGuess);
  const base = baseContentType(response);

  if (base === "application/json" || base.endsWith("+json")) {
    const text = await response.data.text();
    let message = "Report could not be downloaded.";
    let statusCode = response?.status ?? null;
    try {
      const j = JSON.parse(text);
      if (j && j.message) {
        message = j.message;
      }
      if (j && j.statusCode != null) {
        statusCode = j.statusCode;
      }
    } catch (_) {
      /* keep default */
    }
    return {
      ok: false,
      message,
      statusCode,
      limitModal: isDownloadLimitError(message, statusCode, response?.status),
    };
  }

  let blobType = preferredMime;
  switch (base) {
    case MIME_PDF:
      blobType = MIME_PDF;
      break;
    case MIME_XLSX:
      blobType = MIME_XLSX;
      break;
    case MIME_XLSM.toLowerCase():
      blobType = MIME_XLSM;
      break;
    case MIME_XLS:
      blobType = MIME_XLS;
      break;
    case "application/octet-stream":
      blobType = await sniffBlobType(response.data, preferredMime);
      break;
    case "":
      blobType = await sniffBlobType(response.data, preferredMime);
      break;
    default:
      return {
        ok: false,
        message:
          "The report is not available for this study/sample/test. Please explore tests/samples under Asset: Public Data to check the power of this feature.",
        statusCode: response?.status ?? null,
        limitModal: false,
      };
  }

  return {
    ok: true,
    blob: new Blob([response.data], { type: blobType }),
  };
}

export async function executeSampleReportDownload({
  httpGet,
  extension = "pdf",
  syntheticUnavailableMessage,
}) {
  const extLabel = (extension || "pdf").toUpperCase();
  const fallbackDownloadError = `Failed to download ${extLabel} report.`;
  const defaultSyntheticMsg = `The ${extLabel} report is not available for synthetic data in this study/sample/test. Please explore tests/samples under Asset: Public Data to check the power of this feature.`;
  const syntheticMsg = syntheticUnavailableMessage || defaultSyntheticMsg;

  try {
    const response = await httpGet();
    const result = await blobFromSampleReportResponse(response, extension);
    if (!result.ok) {
      if (result.limitModal) {
        showDownloadLimitModal(result.message);
        return { ok: false, limitModal: true };
      }
      const failure = resolveDownloadFailure(
        result.message,
        result.statusCode,
        response?.status
      );
      if (failure.limitModal) {
        return { ok: false, limitModal: true };
      }
      return {
        ok: false,
        snackbarMessage: failure.snackbarMessage || "Report could not be downloaded.",
      };
    }
    const url = URL.createObjectURL(result.blob);
    window.open(url, "_blank");
    return { ok: true };
  } catch (error) {
    const httpStatus = error?.response?.status;
    const { message, statusCode } = await parseDownloadErrorPayload(
      error?.response?.data,
      httpStatus
    );

    if (Number(statusCode ?? httpStatus) >= 500) {
      return { ok: false, snackbarMessage: fallbackDownloadError };
    }

    const failure = resolveDownloadFailure(message, statusCode, httpStatus);
    if (failure.limitModal) {
      return { ok: false, limitModal: true };
    }
    if (failure.snackbarMessage) {
      return { ok: false, snackbarMessage: failure.snackbarMessage };
    }

    if (error?.response) {
      return { ok: false, snackbarMessage: syntheticMsg };
    }
    return { ok: false, snackbarMessage: "Error: Network or server issue." };
  }
}
